import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bug, X, Upload, AlertTriangle, Loader2, Check, Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { uploadBugAttachment, submitBugReport } from '@/lib/api';

const CATEGORIES = [
  { value: 'listing_issue', label: 'Listing issue' },
  { value: 'payment_issue', label: 'Payment issue' },
  { value: 'account_issue', label: 'Account issue' },
  { value: 'search_matching', label: 'Search & matching' },
  { value: 'invoice_billing', label: 'Invoice or billing' },
  { value: 'email_not_received', label: 'Email not received' },
  { value: 'performance_loading', label: 'Performance or loading' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES = [
  { value: 'low', label: 'Low', desc: 'Minor inconvenience', color: 'text-gray-500' },
  { value: 'medium', label: 'Medium', desc: 'Feature not working', color: 'text-amber-500' },
  { value: 'high', label: 'High', desc: 'Blocking my work', color: 'text-orange-500' },
  { value: 'critical', label: 'Critical', desc: 'Data loss or billing issue', color: 'text-red-500' },
];

const MAX_FILES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  if (ua.includes('Firefox/')) browser = `Firefox ${ua.split('Firefox/')[1].split(' ')[0]}`;
  else if (ua.includes('Edg/')) browser = `Edge ${ua.split('Edg/')[1].split(' ')[0]}`;
  else if (ua.includes('Chrome/')) browser = `Chrome ${ua.split('Chrome/')[1].split(' ')[0]}`;
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = `Safari ${ua.split('Version/')[1]?.split(' ')[0] || ''}`;

  let os = 'Unknown';
  if (ua.includes('Win')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return `${browser} | ${os} | ${window.screen.width}x${window.screen.height}`;
}

export const BugReportButton = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadedIds, setUploadedIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    category: '',
    title: '',
    description: '',
    steps_to_reproduce: '',
    severity: 'medium',
    page_url: '',
    browser_info: '',
  });

  useEffect(() => {
    if (open) {
      setForm(f => ({
        ...f,
        page_url: window.location.href,
        browser_info: getBrowserInfo(),
      }));
    }
  }, [open]);

  // Auto-open prefilled from a crash captured by the global ErrorBoundary (admins).
  useEffect(() => {
    if (!user) return;
    let raw;
    try { raw = sessionStorage.getItem('surplify_crash_report'); } catch { return; }
    if (!raw) return;
    try {
      const r = JSON.parse(raw);
      setForm({
        category: 'other',
        title: `App-Absturz: ${(r.message || 'Unbekannter Fehler').slice(0, 80)}`,
        description: `Die Anwendung ist abgestürzt.\n\nFehler: ${r.message || ''}\nSeite: ${r.url || ''}\nZeitpunkt: ${r.time || ''}`.slice(0, 2000),
        steps_to_reproduce: `Stacktrace:\n${(r.stack || '')}\n\nKomponenten-Stack:${r.componentStack || ''}`.slice(0, 1000),
        severity: 'critical',
        page_url: r.url || window.location.href,
        browser_info: getBrowserInfo(),
      });
      setSuccess(null);
      setOpen(true);
    } catch { /* ignore malformed report */ }
    finally {
      try { sessionStorage.removeItem('surplify_crash_report'); } catch { /* noop */ }
    }
  }, [user]);

  const resetForm = () => {
    setForm({ category: '', title: '', description: '', steps_to_reproduce: '', severity: 'medium', page_url: window.location.href, browser_info: getBrowserInfo() });
    setFiles([]);
    setUploadedIds([]);
    setSuccess(null);
  };

  const handleFileAdd = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (files.length + newFiles.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files`);
      return;
    }
    for (const f of newFiles) {
      if (f.size > MAX_FILE_SIZE) { toast.error(`${f.name} too large (max 5MB)`); return; }
      if (!ALLOWED_TYPES.includes(f.type)) { toast.error(`${f.name}: only PNG, JPG, PDF allowed`); return; }
    }

    setUploading(true);
    const ids = [...uploadedIds];
    const allFiles = [...files];
    for (const f of newFiles) {
      try {
        const res = await uploadBugAttachment(f);
        ids.push(res.data.attachment_id);
        allFiles.push({ name: f.name, id: res.data.attachment_id, size: f.size });
      } catch {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploadedIds(ids);
    setFiles(allFiles);
    setUploading(false);
    e.target.value = '';
  };

  const removeFile = (idx) => {
    const newFiles = files.filter((_, i) => i !== idx);
    const newIds = uploadedIds.filter((_, i) => i !== idx);
    setFiles(newFiles);
    setUploadedIds(newIds);
  };

  const handleSubmit = async () => {
    if (!form.category) { toast.error('Please select a category'); return; }
    if (!form.title.trim()) { toast.error('Please enter a title'); return; }
    if (!form.description.trim()) { toast.error('Please enter a description'); return; }

    setSubmitting(true);
    try {
      const res = await submitBugReport({
        ...form,
        attachment_ids: uploadedIds,
      });
      setSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { resetForm(); setOpen(true); }}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-full shadow-lg transition-all hover:shadow-xl group"
        data-testid="bug-report-fab"
      >
        <Bug className="h-5 w-5" />
        <span className="text-sm font-medium hidden sm:inline">Report a Problem</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="bug-report-modal">
          {success ? (
            /* ── Success screen ── */
            <div className="text-center py-8 space-y-4" data-testid="bug-report-success">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold">Report Submitted</h2>
              <p className="text-lg font-mono font-semibold text-orange-500" data-testid="bug-report-id">{success.report_id}</p>
              <p className="text-sm text-muted-foreground">We will review your report within <strong>2 business days</strong>.</p>
              <Button onClick={() => setOpen(false)} className="mt-4">Close</Button>
            </div>
          ) : (
            /* ── Report form ── */
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bug className="h-5 w-5 text-orange-500" />
                  Report a Problem
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Category */}
                <div>
                  <Label>Category *</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="bug-category-select">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Title */}
                <div>
                  <Label>Title * <span className="text-xs text-muted-foreground">({form.title.length}/100)</span></Label>
                  <Input
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value.slice(0, 100) }))}
                    placeholder="Short summary of the problem"
                    data-testid="bug-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label>Description * <span className="text-xs text-muted-foreground">({form.description.length}/2000)</span></Label>
                  <Textarea
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value.slice(0, 2000) }))}
                    placeholder="What happened? What did you expect? What did you see?"
                    rows={4}
                    data-testid="bug-description-input"
                  />
                </div>

                {/* Steps to reproduce */}
                <div>
                  <Label>Steps to Reproduce <span className="text-xs text-muted-foreground">(optional, {form.steps_to_reproduce.length}/1000)</span></Label>
                  <Textarea
                    value={form.steps_to_reproduce}
                    onChange={e => setForm(f => ({ ...f, steps_to_reproduce: e.target.value.slice(0, 1000) }))}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                    rows={3}
                    data-testid="bug-steps-input"
                  />
                </div>

                {/* Severity */}
                <div>
                  <Label>Severity *</Label>
                  <RadioGroup
                    value={form.severity}
                    onValueChange={v => setForm(f => ({ ...f, severity: v }))}
                    className="grid grid-cols-2 gap-2 mt-1"
                    data-testid="bug-severity-group"
                  >
                    {SEVERITIES.map(s => (
                      <label
                        key={s.value}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${form.severity === s.value ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'hover:border-slate-300'}`}
                        data-testid={`bug-severity-${s.value}`}
                      >
                        <RadioGroupItem value={s.value} />
                        <div>
                          <p className={`text-sm font-medium ${s.color}`}>{s.label}</p>
                          <p className="text-xs text-muted-foreground">{s.desc}</p>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {/* Attachments */}
                <div>
                  <Label>Attachments <span className="text-xs text-muted-foreground">(max 3 files, 5MB each — PNG, JPG, PDF)</span></Label>
                  <div className="mt-1 space-y-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 dark:bg-slate-800 px-3 py-2 rounded" data-testid={`bug-attachment-${i}`}>
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <button onClick={() => removeFile(i)} className="text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                    {files.length < MAX_FILES && (
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed rounded cursor-pointer text-sm text-muted-foreground hover:border-orange-400 transition-colors">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        <span>{uploading ? 'Uploading...' : 'Add file'}</span>
                        <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.pdf" onChange={handleFileAdd} disabled={uploading} data-testid="bug-file-input" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Page URL */}
                <div>
                  <Label>Current Page URL</Label>
                  <Input
                    value={form.page_url}
                    onChange={e => setForm(f => ({ ...f, page_url: e.target.value }))}
                    data-testid="bug-url-input"
                  />
                </div>

                {/* Browser info */}
                <div>
                  <Label>Browser & Device Info</Label>
                  <Input value={form.browser_info} readOnly className="bg-slate-50 dark:bg-slate-800 text-muted-foreground" data-testid="bug-browser-info" />
                </div>

                {/* Submit */}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !form.category || !form.title.trim() || !form.description.trim()}
                  className="w-full bg-orange-500 hover:bg-orange-600 h-11"
                  data-testid="bug-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                  Send Report
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
